/* ==========================================================================
   Custom — JS global (kit pipeline)
   Chargé via snippets/custom-globals.liquid. Script classique (pas de module),
   volontairement indépendant de l'importmap d'Horizon pour survivre aux updates.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Sélecteur de vue des cartes (« Produit seul » / « En situation ») — pages collection
   Menu déroulant (refonte 2026-10-21, modèle Y2K Avenue). « En situation » bascule
   toutes les cartes produit sur leur 2e image, déjà présente dans le DOM grâce au
   réglage natif « show_second_image_on_hover » (snippets/card-gallery.liquid).
   Le choix est mémorisé dans localStorage (même clé que l'ancien toggle). En vue
   « En situation », l'effet de survol est neutralisé : l'image reste figée.
   Markup : blocks/custom-model-view-toggle.liquid (copié dans chaque barre de filtres).
   -------------------------------------------------------------------------- */
(() => {
  'use strict';

  const STORAGE_KEY = 'custom:model-view';
  const ROOT_CLASS = 'custom-model-view';
  const WRAPPER_SELECTOR = '.facets-controls-wrapper';

  const readPref = () => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch (error) {
      return false;
    }
  };

  const writePref = (on) => {
    try {
      localStorage.setItem(STORAGE_KEY, on ? '1' : '0');
    } catch (error) {
      /* navigation privée ou stockage bloqué : le sélecteur marche quand même, sans mémoire */
    }
  };

  let situation = readPref();

  /** Bascule une carte sur sa 2e image, ou la remet sur la première. Cartes à une image ignorées. */
  const applyToCard = (card) => {
    const gallery = card.querySelector('[ref="cardGallery"]');
    const slideshow = card.querySelector('slideshow-component');
    if (!gallery || !slideshow || typeof slideshow.select !== 'function') return;
    const slides = slideshow.slides;
    if (!slides || slides.length < 2) return;

    if (situation) {
      if (gallery.hasAttribute('on:pointerenter')) {
        gallery.dataset.customHoverEnter = gallery.getAttribute('on:pointerenter');
        gallery.dataset.customHoverLeave = gallery.getAttribute('on:pointerleave') || '';
        gallery.removeAttribute('on:pointerenter');
        gallery.removeAttribute('on:pointerleave');
      }
      if (slideshow.current !== 1) slideshow.select(1, undefined, { animate: false });
    } else {
      if ('customHoverEnter' in gallery.dataset) {
        gallery.setAttribute('on:pointerenter', gallery.dataset.customHoverEnter);
        if (gallery.dataset.customHoverLeave) gallery.setAttribute('on:pointerleave', gallery.dataset.customHoverLeave);
        delete gallery.dataset.customHoverEnter;
        delete gallery.dataset.customHoverLeave;
      }
      if (slideshow.current !== 0) slideshow.select(0, undefined, { animate: false });
    }
  };

  const applyAll = () => {
    document.querySelectorAll('product-card').forEach(applyToCard);
  };

  /** Met chaque sélecteur à jour : libellé courant, option cochée. */
  const sync = () => {
    const value = situation ? 'situation' : 'product';
    document.querySelectorAll('.' + ROOT_CLASS).forEach((root) => {
      root.dataset.view = value;
      let label = '';
      root.querySelectorAll('[role="option"]').forEach((option) => {
        const selected = option.dataset.value === value;
        option.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) {
          const main = option.querySelector('.custom-model-view__label');
          label = main ? (main.firstChild && main.firstChild.nodeType === 3 ? main.firstChild.textContent : main.textContent) : '';
        }
      });
      const current = root.querySelector('.custom-model-view__current');
      if (current && label) current.textContent = label.trim();
    });
  };

  const options = (root) => Array.from(root.querySelectorAll('[role="option"]'));

  const close = (root, focusButton) => {
    const button = root.querySelector('.custom-model-view__button');
    const list = root.querySelector('.custom-model-view__list');
    if (list) list.hidden = true;
    if (button) button.setAttribute('aria-expanded', 'false');
    root.classList.remove('is-open');
    if (focusButton && button) button.focus();
  };

  const closeAll = (except) => {
    document.querySelectorAll('.' + ROOT_CLASS + '.is-open').forEach((root) => {
      if (root !== except) close(root, false);
    });
  };

  const open = (root) => {
    closeAll(root);
    const button = root.querySelector('.custom-model-view__button');
    const list = root.querySelector('.custom-model-view__list');
    if (!button || !list) return;
    list.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    root.classList.add('is-open');
    const opts = options(root);
    (opts.find((o) => o.getAttribute('aria-selected') === 'true') || opts[0])?.focus();
  };

  const choose = (root, value) => {
    close(root, true);
    situation = value === 'situation';
    writePref(situation);
    sync();
    applyAll();
  };

  /* Un seul écouteur délégué : les copies posées dans les barres sont recréées à chaque re-rendu. */
  const onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const root = target ? target.closest('.' + ROOT_CLASS) : null;
    if (!root || !root.closest(WRAPPER_SELECTOR)) {
      closeAll();
      return;
    }
    const option = target.closest('[role="option"]');
    if (option) {
      choose(root, option.dataset.value);
      return;
    }
    if (target.closest('.custom-model-view__button')) {
      if (root.classList.contains('is-open')) close(root, false);
      else open(root);
    }
  };

  const onKeydown = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const root = target ? target.closest('.' + ROOT_CLASS) : null;
    if (!root) return;
    const isOpen = root.classList.contains('is-open');
    const opts = options(root);
    const index = opts.indexOf(target.closest('[role="option"]'));

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp': {
        event.preventDefault();
        if (!isOpen) {
          open(root);
          return;
        }
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next = index === -1 ? 0 : (index + step + opts.length) % opts.length;
        opts[next]?.focus();
        break;
      }
      case 'Enter':
      case ' ':
        if (index > -1) {
          event.preventDefault();
          choose(root, opts[index].dataset.value);
        }
        break;
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          close(root, true);
        }
        break;
      case 'Tab':
        if (isOpen) close(root, false);
        break;
      default:
        break;
    }
  };

  /** Repli : markup identique au bloc blocks/custom-model-view-toggle.liquid (sans icônes). */
  const buildSelector = () => {
    const root = document.createElement('div');
    root.className = ROOT_CLASS;
    root.dataset.view = 'product';
    const option = (value, label, hint, selected) =>
      '<li class="custom-model-view__option" role="option" tabindex="-1" data-value="' + value + '" aria-selected="' + selected + '">' +
      '<span class="custom-model-view__label">' + label + '</span>' +
      '<span class="custom-model-view__hint">' + hint + '</span></li>';
    root.innerHTML =
      '<button type="button" class="custom-model-view__button" aria-haspopup="listbox" aria-expanded="false">' +
      '<span class="custom-model-view__eyebrow">Vue</span>' +
      '<span class="custom-model-view__current">Produit seul</span>' +
      '<svg class="custom-model-view__chevron" viewBox="0 0 10 6" aria-hidden="true" focusable="false"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.2"/></svg>' +
      '</button>' +
      '<ul class="custom-model-view__list" role="listbox" aria-label="Vue" hidden>' +
      option('product', 'Produit seul', 'Le luminaire sur fond neutre', 'true') +
      option('situation', 'En situation', 'Allumé, dans la pièce', 'false') +
      '</ul>';
    return root;
  };

  /*
    Le sélecteur n'existe que sur les pages collection, dans l'en-tête des filtres.
    Horizon rend deux barres (.facets-controls-wrapper) : ordinateur et mobile. Chacune reçoit
    une copie du bloc posé sur la page (il porte les libellés) ; sinon un sélecteur par défaut.
  */
  const inject = () => {
    const collection = document.querySelector('collection-component');
    if (!collection) return;
    const source = document.querySelector('.' + ROOT_CLASS + ':not(' + WRAPPER_SELECTOR + ' *)');
    collection.querySelectorAll(WRAPPER_SELECTOR).forEach((wrapper) => {
      if (wrapper.querySelector('.' + ROOT_CLASS)) return;
      wrapper.appendChild(source ? source.cloneNode(true) : buildSelector());
    });
    sync();
  };

  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      inject();
      applyAll();
    });
  };

  const init = async () => {
    if (!document.querySelector('collection-component')) return;
    await Promise.all([customElements.whenDefined('slideshow-component'), customElements.whenDefined('product-card')]);
    inject();
    applyAll();
    const list = document.querySelector('results-list');
    if (list) new MutationObserver(schedule).observe(list, { childList: true, subtree: true });
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeydown);
    document.addEventListener('shopify:section:load', () => {
      document.querySelectorAll(WRAPPER_SELECTOR + ' .' + ROOT_CLASS).forEach((root) => root.remove());
      schedule();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* --------------------------------------------------------------------------
   V2 Claude Design (2026-10-18)
   1. Livraison estimée : blocks/custom-delivery-estimate.liquid
      « Livrée entre le … et le … » en jours ouvrés (lundi-vendredi).
   2. Offre quantité : blocks/custom-bundle-offer.liquid
      Le choix 1 / 2 / 3 règle le champ quantité du formulaire produit
      (input[name="quantity"] de la même section). Aucune remise appliquée ici :
      ce sont les réductions automatiques Shopify qui s'en chargent au panier.
   -------------------------------------------------------------------------- */
(() => {
  const addBusinessDays = (date, days) => {
    const d = new Date(date);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return d;
  };

  const formatDay = (date) =>
    date
      .toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });

  const initDeliveryEstimates = () => {
    document.querySelectorAll('[data-custom-delivery]').forEach((el) => {
      const min = parseInt(el.dataset.min, 10);
      const max = parseInt(el.dataset.max, 10);
      if (Number.isNaN(min) || Number.isNaN(max)) return;
      const today = new Date();
      const from = addBusinessDays(today, min);
      const to = addBusinessDays(today, max);
      el.textContent = `${el.dataset.prefix} ${formatDay(from)} ${el.dataset.between} ${formatDay(to)}`;
    });
  };

  const setQuantity = (fieldset, qty) => {
    const scope = fieldset.closest('.shopify-section') || document;
    const inputs = scope.querySelectorAll('input[name="quantity"]');
    inputs.forEach((input) => {
      input.value = String(qty);
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  };

  const onBundleChange = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || radio.type !== 'radio') return;
    const fieldset = radio.closest('[data-custom-bundle]');
    if (!fieldset) return;
    setQuantity(fieldset, parseInt(radio.value, 10) || 1);
  };

  const init = () => {
    initDeliveryEstimates();
    document.addEventListener('change', onBundleChange);
  };

  // Le bloc est re-rendu quand on change de variante (morph Horizon) : on recalcule les dates.
  document.addEventListener('shopify:section:load', initDeliveryEstimates);
  new MutationObserver(() => {
    document.querySelectorAll('[data-custom-delivery]').forEach((el) => {
      if (!el.dataset.done || el.textContent.indexOf(el.dataset.prefix) !== 0) {
        el.dataset.done = '1';
        initDeliveryEstimates();
      }
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* --------------------------------------------------------------------------
   V2 Claude Design : guide de collection (blocks/custom-collection-guide.liquid)
   Replie le guide au-delà de la hauteur réglée et active le bouton
   « Lire tout le guide ». Sans JS, le guide reste entièrement visible.
   -------------------------------------------------------------------------- */
(() => {
  const initCollectionGuides = () => {
    document.querySelectorAll('[data-custom-guide]:not([data-guide-ready])').forEach((guide) => {
      guide.dataset.guideReady = '1';
      const body = guide.querySelector('.custom-guide__body');
      const button = guide.querySelector('.custom-guide__toggle');
      if (!body || !button) return;

      const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const limit = (parseFloat(guide.dataset.collapsedHeight) || 32) * rem;
      if (body.scrollHeight <= limit * 1.25) return;

      guide.style.setProperty('--custom-guide-collapsed', `${limit}px`);
      guide.classList.add('is-collapsed');
      button.hidden = false;

      button.addEventListener('click', () => {
        const open = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', String(!open));
        guide.classList.toggle('is-collapsed', open);
        button.textContent = open ? button.dataset.labelOpen : button.dataset.labelClose;
        if (open) guide.scrollIntoView({ block: 'start', behavior: 'smooth' });
      });
    });
  };

  document.addEventListener('shopify:section:load', initCollectionGuides);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollectionGuides);
  } else {
    initCollectionGuides();
  }
})();

/* --------------------------------------------------------------------------
   Barre d'ajout au panier collante : surtitre = nom japonais du produit
   (2026-10-18). Lit le bloc texte du nom japonais de la fiche (clé de bloc
   text_9DjeKg dans templates/product.json) et le pose en variable CSS
   --custom-sticky-eyebrow sur <sticky-add-to-cart>. L'hôte n'est pas
   « morphé » au changement de variante : la valeur reste. Styles :
   assets/custom.css, bloc 14. Propre à Yugure (clé de bloc), non reporté.
   -------------------------------------------------------------------------- */
(() => {
  'use strict';

  const SOURCE = '.text-block[class*="__text_9DjeKg"]';

  const init = () => {
    const host = document.querySelector('sticky-add-to-cart');
    const source = document.querySelector(SOURCE);
    if (!host || !source) return;
    const name = source.textContent.trim();
    if (!name) return;
    host.style.setProperty('--custom-sticky-eyebrow', JSON.stringify(name));
    host.setAttribute('data-custom-eyebrow', '');
  };

  document.addEventListener('shopify:section:load', init);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* --------------------------------------------------------------------------
   Offre quantité (blocks/custom-bundle-offer.liquid) : prix suivant la variante
   Les prix 1 / 2 / 3 luminaires sont calculés en Liquid sur la variante
   sélectionnée, mais Horizon ne re-rend pas ce bloc au changement de variante.
   On écoute l'événement standard shopify:product:select (comme product-price.js),
   on attend le HTML de section renvoyé et on y reprend les prix du même bloc
   (data-block-id). Si l'offre disparaît pour la variante (indisponible),
   le bloc est masqué. (2026-10-21)
   -------------------------------------------------------------------------- */
(() => {
  document.addEventListener('shopify:product:select', (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('product-card')) return;
    const scope = target.closest('.shopify-section, dialog');
    if (!scope || !event.promise) return;
    const bundles = scope.querySelectorAll('[data-custom-bundle][data-block-id]');
    if (!bundles.length) return;

    event.promise
      .then((result) => {
        const html = result && result.detail && result.detail.html;
        if (!html || typeof html.querySelector !== 'function') return;
        bundles.forEach((bundle) => {
          const next = html.querySelector(`[data-custom-bundle][data-block-id="${bundle.dataset.blockId}"]`);
          if (!next) {
            bundle.style.display = 'none';
            return;
          }
          bundle.style.display = '';
          const prices = bundle.querySelectorAll('.custom-bundle__price');
          const nextPrices = next.querySelectorAll('.custom-bundle__price');
          prices.forEach((price, i) => {
            if (nextPrices[i]) price.innerHTML = nextPrices[i].innerHTML;
          });
        });
      })
      .catch(() => {});
  });
})();
